// Presentation only: nothing here fetches.
import type { ReactNode } from "react";
import { HelpTooltip } from "./HelpTooltip";
import { Link, useSearchParams } from "react-router";
import { presentError } from "./errorPresentation";
import { ErrorExplanation } from "./errors";
import { btn } from "./actions";
import { Glyph } from "./icons";
import { StatusLabel } from "./status";
import { WorkspaceTabs } from "./workspace";
import { exactTime, relativeTime } from "@/lib/format";

/** A panel: a quiet header rule, then the content. `.card-body` un-pads itself for a bare table. */
export function Card({ title, subtitle, children, actions }: {
  title?: ReactNode; subtitle?: ReactNode; children: ReactNode; actions?: ReactNode;
}) {
  return (
    <section className="celia-card purpose-card overflow-hidden rounded-xl border border-hair bg-surface">
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-hair px-4 py-3">
          <div className="min-w-0">
            {title && <h2 className="text-sm font-semibold tracking-tight text-ink truncate">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-muted">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className="card-body">{children}</div>
    </section>
  );
}

/** The title of a list page: what it is, how many, and the action that adds to it. `help` is the optional
 *  "how this works" text, one `?` beside the title instead of a paragraph under it; `description` is a short
 *  identifying line (a project or owner name), never an explanation. `count` and `status` sit beside the title. */
export function PageHeader({ title, description, help, actions, count, status }: {
  title: ReactNode; description?: ReactNode; help?: string; actions?: ReactNode; count?: ReactNode; status?: ReactNode;
}) {
  return (
    <div className="celia-page-header flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="min-w-0 flex-[1_1_18rem]">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <h1 className="celia-page-title flex min-w-0 items-center gap-1">{title}</h1>
          {count != null && <span className="celia-page-count">{count}</span>}
          {status}
          {help && <HelpTooltip label="this page">{help}</HelpTooltip>}
        </div>
        {description && <p className="mt-0.5 truncate text-[13px] text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

const FILLED: Record<string, string> = {
  ok: "bg-second text-on-fill", warn: "bg-amber text-on-fill", bad: "bg-danger text-on-fill",
  muted: "bg-muted text-on-fill", accent: "bg-accent text-on-accent",
};

// A state badge reads as the shared status label (dot + text), so a page never shows two chip systems.
// `filled` is the selected/active treatment for a badge used as a toggle.
export function Badge({ tone = "muted", title, filled, children }: { tone?: string; title?: string; filled?: boolean; children: ReactNode }) {
  if (!filled) {
    const known = tone === "ok" || tone === "warn" || tone === "bad" || tone === "accent" ? tone : "muted";
    return <StatusLabel tone={known} title={title}>{children}</StatusLabel>;
  }
  return (
    <span title={title} className={`inline-flex h-[22px] items-center whitespace-nowrap rounded-[5px] px-[7px] text-[11px] font-medium ${FILLED[tone] ?? FILLED.muted}`}>
      {children}
    </span>
  );
}

/** A recorded instant: relative in the row ("3 min ago", "Oct 4"), exact UTC on hover and in `dateTime`.
 *  `exact` prints the full instant where the time itself is the evidence (a log, a timing table). */
export function Time({ at, exact }: { at: string | null | undefined; exact?: boolean }) {
  if (!at) return <span className="text-muted">—</span>;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return <span className="text-muted">—</span>;
  return <time dateTime={d.toISOString()} title={exactTime(d)} className={`whitespace-nowrap ${exact ? "text-muted" : "text-quiet tabular-nums"}`}>{exact ? exactTime(d) : relativeTime(d)}</time>;
}

/** Nothing exists yet. `icon` names a Material Symbol; `action` offers the one way to start. */
export function Empty({ children, icon, action }: { children: ReactNode; icon?: string; action?: ReactNode }) {
  if (!icon && !action) return <p className="px-4 py-8 text-center text-sm text-muted">{children}</p>;
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-muted">
      {icon && <span className="grid h-10 w-10 place-items-center rounded-full bg-raise text-muted"><Glyph name={icon} size={20} /></span>}
      <p>{children}</p>
      {action}
    </div>
  );
}

// Nothing matches the active filter — distinct from Empty (nothing exists), and it offers a way out.
export function FilteredEmpty({ children, onClear }: { children?: ReactNode; onClear?: () => void }) {
  return (
    <div className="px-4 py-8 text-center text-sm text-muted">
      <p>{children ?? "Nothing matches the current filter."}</p>
      {onClear && <button type="button" onClick={onClear} className="mt-1 font-medium text-accent-ink hover:underline">Clear filter</button>}
    </div>
  );
}

// A feature or object that exists but can't be shown here (deleted bytes, a disabled capability).
export function Unavailable({ children }: { children: ReactNode }) {
  return <p className="rounded-md border border-dashed border-hair px-3 py-8 text-center text-sm text-muted">{children}</p>;
}

export function Crumbs({ items }: { items: { href?: string; label: string }[] }) {
  return (
    <nav className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <span aria-hidden className="text-muted/50">/</span>}
          {it.href ? <Link to={it.href} className="text-muted hover:text-ink">{it.label}</Link> : <span className="text-body">{it.label}</span>}
        </span>
      ))}
    </nav>
  );
}

// The identity block every detail page shares. It sits above the tabs, so the object,
// its exact version/draft id and its baseline stay visible while tabs and evidence change
// (guide §3). `kind` distinguishes a line from an immutable version from a view.
export function ObjectHeader({ title, kind, id, status, badges, baseline, live, actions }: {
  crumbs?: { href?: string; label: string }[];
  title: ReactNode;
  kind?: ReactNode;
  id?: ReactNode;
  status?: ReactNode;
  badges?: ReactNode;
  baseline?: ReactNode;
  live?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="celia-object-header space-y-2">
      {/* The shell toolbar owns the breadcrumb trail; `crumbs` stays accepted for callers but is not repeated here. */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0 space-y-1.5">
          <h1 className="celia-page-title celia-object-title text-[22px] leading-7">{typeof title === "string" ? <span>{title}</span> : title}{status}{badges}{live}</h1>
          {(kind || id || baseline) && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
              {kind && (typeof kind === "string" ? <span className="meta-chip">{kind}</span> : kind)}
              {id && <span className="mono">{id}</span>}
              {baseline && <span className="text-body">{baseline}</span>}
            </div>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function Kv({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[minmax(5rem,9rem)_1fr] gap-x-5 gap-y-2 text-sm">
      {rows.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-xs leading-5 text-muted">{k}</dt>
          <dd className="min-w-0 break-words text-ink">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Short({ value, n = 8 }: { value: string | null | undefined; n?: number }) {
  return value ? <span className="mono" title={value}>{value.slice(0, n)}</span> : <span className="text-muted">—</span>;
}

/** Points of change between two fractions, signed. */
export function Delta({ v, warnAt }: { v: number | null | undefined; warnAt?: number }) {
  if (v === null || v === undefined) return <span className="text-muted">—</span>;
  const pts = v * 100;
  const tone = warnAt !== undefined && -v >= warnAt ? "text-danger" : pts > 0 ? "text-second" : pts < 0 ? "text-amber" : "text-muted";
  return <span className={`mono font-medium ${tone}`}>{pts > 0 ? "+" : ""}{pts.toFixed(1)}</span>;
}

// what happened · why it matters · the action available (guide §6). The blocking tone keeps
// text-danger on the message, which is what a failing check reads off the page.
export function Callout({ tone = "muted", title, children, actions, testid }: {
  tone?: "ok" | "warn" | "bad" | "muted" | "accent"; title?: ReactNode; children?: ReactNode; actions?: ReactNode; testid?: string;
}) {
  const skin = {
    ok: "border-second/30 bg-second/6", warn: "border-amber/30 bg-amber/6", bad: "border-danger/30 bg-danger/6",
    muted: "border-hair bg-raise", accent: "border-accent/30 bg-accent/6",
  }[tone];
  const ink = { ok: "text-second", warn: "text-amber", bad: "text-danger", muted: "text-ink", accent: "text-accent-ink" }[tone];
  return (
    <div role={tone === "bad" ? "alert" : undefined} data-testid={testid} className={`rounded-md border px-3 py-2.5 text-sm ${skin}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          {title && <p className={`font-semibold ${ink}`}>{title}</p>}
          {children && <div className={title ? "mt-0.5 text-body" : ink}>{children}</div>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function JsonBlock({ value, summary = "raw" }: { value: unknown; summary?: string }) {
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted hover:text-ink">{summary}</summary>
      <pre className="mono mt-2 max-h-96 overflow-auto rounded-md border border-hair bg-raise p-3 leading-relaxed">{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

/** Tabs driven by a `tab` query parameter, so every tab is addressable. */
export function useTab(first: string): string {
  const [params] = useSearchParams();
  return params.get("tab") ?? first;
}

/** The same underline tabs as `WorkspaceTabs`, for pages whose sections sit outside a workspace frame. */
export function Tabs({ current, tabs }: { current: string; tabs: [string, ReactNode][] }) {
  return <WorkspaceTabs current={current} tabs={tabs} label="Page sections" className="celia-page-tabs border-b border-hair" testId="tabs" />;
}

type Finding = { severity?: string; code: string; message: string; count?: number | null; examples?: string[] };

export function Issues({ items }: { items: Finding[] }) {
  if (!items.length) return <Empty>Nothing to report.</Empty>;
  return (
    <table className="grid-table" data-testid="issues">
      <thead><tr><th>Severity</th><th>Code</th><th>Message</th><th>Count</th><th>Examples</th></tr></thead>
      <tbody>{items.map((i, k) => (
        <tr key={k}>
          <td><Badge tone={i.severity === "block" ? "bad" : "warn"}>{i.severity ?? "warn"}</Badge></td>
          <td className="mono text-xs">{i.code}</td><td>{i.message}</td><td>{i.count ?? ""}</td>
          <td className="mono text-xs text-muted">{(i.examples ?? []).slice(0, 5).join(", ")}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

export function Bars({ rows, total }: { rows: [string, number][]; total?: number }) {
  const max = total ?? Math.max(1, ...rows.map(([, n]) => n));
  return (
    <table className="w-full text-sm">
      <tbody>{rows.map(([k, n]) => (
        <tr key={k}>
          <td className="w-40 truncate py-1 pr-3 text-muted" title={k}>{k}</td>
          <td><div className="h-1.5 rounded-full bg-accent/70" style={{ width: `${Math.max(1, (n / max) * 100)}%` }} /></td>
          <td className="mono w-20 py-1 pl-3 text-right tabular-nums text-ink">{n}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

export function Loading() {
  return <div role="status" className="celia-loading">
    <span className="sr-only">Loading…</span>
    <span aria-hidden="true" className="celia-loading-mark"><Glyph name="progress_activity" size={20} /></span>
    <span aria-hidden="true" className="celia-loading-lines"><span className="celia-skeleton" /><span className="celia-skeleton" /><span className="celia-skeleton" /></span>
  </div>;
}

// A supplied callback remains under owner control; this presentation never automatically replays a request.
export function Failed({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const copy = presentError(error, { activity: onRetry ? "read" : "request", offline: typeof navigator !== "undefined" && navigator.onLine === false });
  const reload = onRetry && (copy.recovery === "retry" || copy.recovery === "refresh" || copy.recovery === "wait");
  return (
    <Callout tone={copy.tone} title={copy.title} actions={reload ? (
      <button type="button" onClick={onRetry} className={btn("plain", "row")}>{copy.recovery === "retry" ? "Reload details" : "Refresh status"}</button>
    ) : undefined}>
      <ErrorExplanation presentation={copy} />
    </Callout>
  );
}
